import React, { useEffect, useState, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CircularProgress, Pagination, Stack, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import notificationsService from "../../services/notificationsService";

const PushMessageType = {
    General: 0,
    PwsStatusChanged: 1,
    InvoiceAdded: 2,
    InvoicePaymentAdded: 3,
};

const formatTime = (isoString) => DateTime.fromISO(isoString).toFormat('HH:mm');

const formatDateHeader = (isoString) => {
    const dt = DateTime.fromISO(isoString).setLocale('ru');
    const today = DateTime.now().startOf('day');
    const date = dt.startOf('day');
    const diff = today.diff(date, 'days').days;
    if (diff < 1) return 'Сегодня';
    if (diff < 2) return 'Вчера';
    return dt.toFormat('d MMMM yyyy');
};

const groupByDate = (items) => {
    const groups = [];
    let lastDate = null;
    for (const item of items) {
        const dateKey = DateTime.fromISO(item.createdAtUtc).toFormat('yyyy-MM-dd');
        if (dateKey !== lastDate) {
            lastDate = dateKey;
            groups.push({ dateKey, label: formatDateHeader(item.createdAtUtc), items: [] });
        }
        groups[groups.length - 1].items.push(item);
    }
    return groups;
};

const NotificationItem = ({ notification, onRead }) => {
    const isRead = !!notification.readAtUtc;

    return (
        <div
            className={`notification_item ${isRead ? 'notification_item_read' : ''}`}
            onClick={() => onRead(notification)}
        >
            <div className="notification_item_accent" />
            <div className="notification_item_body">
                <div className="notification_item_time">{formatTime(notification.createdAtUtc)}</div>
                <div className="notification_item_title">{notification.title}</div>
                <div className="notification_item_description">{notification.description}</div>
            </div>
        </div>
    );
};

const GeneralNotificationModal = ({ notification, onClose }) => {
    if (!notification) return null;
    return (
        <Dialog open={!!notification} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{notification.title}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ whiteSpace: 'pre-wrap' }}>
                    {notification.description}
                </DialogContentText>
                <DialogContentText sx={{ mt: 2, fontSize: '0.8rem', color: 'text.disabled', textAlign: 'right' }}>
                    {DateTime.fromISO(notification.createdAtUtc).setLocale('ru').toFormat("d MMMM yyyy 'в' HH:mm")}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={onClose}>Закрыть</Button>
            </DialogActions>
        </Dialog>
    );
};

export const NotificationsIndex = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const currentUserInfo = useSelector((state) => state.profileState.currentUserInfo);
    const unreadNotificationsCount = useSelector((state) => state.mainMenuState.unreadNotificationsCount ?? 0);

    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [generalModalNotification, setGeneralModalNotification] = useState(null);

    useEffect(() => {
        dispatch({ type: 'CURRENT_PAGE_CHANGED', currentPage: 'notifications' });
    }, [dispatch]);

    const loadNotifications = useCallback((page) => {
        if (!currentUserInfo?.id) return;
        setIsLoading(true);
        notificationsService.getNotifications(currentUserInfo.id, page)
            .then((result) => {
                setNotifications(result.items ?? []);
                setTotalPages(result.totalPages ?? 1);
            })
            .catch(() => enqueueSnackbar('Ошибка загрузки уведомлений', { variant: 'error' }))
            .finally(() => setIsLoading(false));
    }, [currentUserInfo, enqueueSnackbar]);

    useEffect(() => {
        loadNotifications(currentPage);
    }, [currentPage, loadNotifications]);

    const markAsRead = useCallback((notification) => {
        if (!notification.readAtUtc) {
            notificationsService.markAsRead(notification.id)
                .then(() => {
                    setNotifications((prev) =>
                        prev.map((n) => n.id === notification.id
                            ? { ...n, readAtUtc: new Date().toISOString() }
                            : n
                        )
                    );
                    dispatch({
                        type: 'UNREAD_NOTIFICATIONS_COUNT_LOADED',
                        unreadNotificationsCount: Math.max(0, unreadNotificationsCount - 1)
                    });
                })
                .catch(() => {});
        }
    }, [dispatch, unreadNotificationsCount]);

    const handleNotificationClick = useCallback((notification) => {
        markAsRead(notification);
        const type = notification.payloadData?.Type;
        switch (type) {
            case PushMessageType.General:
                setGeneralModalNotification(notification);
                break;
            case PushMessageType.PwsStatusChanged:
                navigate('/purchases-in-work');
                break;
            case PushMessageType.InvoiceAdded:
            case PushMessageType.InvoicePaymentAdded:
                navigate('/invoices');
                break;
            default:
                setGeneralModalNotification(notification);
                break;
        }
    }, [markAsRead, navigate]);

    const groups = groupByDate(notifications);

    return (
        <React.Fragment>
            <div className="notifications_page">
                <div className="notifications_list_container">
                    {isLoading ? (
                        <div className="notifications_loading">
                            <CircularProgress />
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="no_item_block">
                            <div className="no_item_block_text_main">Нет уведомлений</div>
                        </div>
                    ) : (
                        <>
                            {groups.map((group) => (
                                <div key={group.dateKey} className="notifications_group">
                                    <div className="notifications_date_divider">
                                        <span>{group.label}</span>
                                    </div>
                                    {group.items.map((n) => (
                                        <NotificationItem
                                            key={n.id}
                                            notification={n}
                                            onRead={handleNotificationClick}
                                        />
                                    ))}
                                </div>
                            ))}
                            <Stack sx={{ mt: 3, alignItems: 'center' }}>
                                <Pagination
                                    page={currentPage}
                                    count={totalPages}
                                    onChange={(_, p) => setCurrentPage(p)}
                                />
                            </Stack>
                        </>
                    )}
                </div>
            </div>

            <GeneralNotificationModal
                notification={generalModalNotification}
                onClose={() => setGeneralModalNotification(null)}
            />
        </React.Fragment>
    );
};
